import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo } from "react";
import { FixedSizeList } from "react-window";
import styled from "styled-components";
import { defaultControlSize } from "../../newCommon/ControlsCommon";
import { cssBody400Bold } from "../../typography/styles";
import { useHighlightState } from "../hooks/useHighlightState";
import { useOptionsScroll } from "../hooks/useOptionsScroll";
import { defaultDropdownHeight, defaultItemSize } from "../renders/default";
import { InfiniteScroll } from "./InfiniteScroll";
const _SelectDropdownOptions = ({ renderOption, groups, onInfiniteScroll, infiniteScrollLoading, ...props }) => {
    const { options = [], size = defaultControlSize, getOptionIsActive, toggleOptionByValue, optionsMap, value, dropdownRef, virtualRender, virtualItemSize = defaultItemSize, dropdownHeight = defaultDropdownHeight, } = props;
    const handleEnter = useCallback((highlighted) => {
        const option = optionsMap[highlighted];
        if (!option || option.disabled)
            return;
        if (typeof option.onClick === "function") {
            option.onClick?.();
        }
        else {
            toggleOptionByValue(highlighted);
        }
    }, [optionsMap, toggleOptionByValue]);
    const { getOptionsIsHighlighted, setControlledHighlight, highlighted } = useHighlightState({
        options,
        onSelect: handleEnter,
    });
    useOptionsScroll({
        dropdownRef,
        highlighted,
        value,
    });
    const handleOptionClick = useCallback((event) => {
        if (event) {
            toggleOptionByValue(event.currentTarget.dataset.value || "");
        }
    }, [toggleOptionByValue]);
    const getOptionProps = (option) => {
        return {
            option,
            size,
            disabled: option.disabled,
            hover: option.hover ?? getOptionsIsHighlighted(option),
            active: option.active ?? getOptionIsActive(option),
            onClick: !option.disabled ? option.onClick || handleOptionClick : undefined,
        };
    };
    const getOptions = (options) => {
        if (virtualRender) {
            return (_jsx(FixedSizeList, { height: dropdownHeight, width: "100%", itemCount: options.length, itemSize: virtualItemSize, children: ({ index, style }) => {
                    const option = options[index];
                    const optionsProps = getOptionProps(option);
                    return (_jsxs("div", { style: style, children: [option?.render?.(optionsProps) || renderOption?.(optionsProps), index + 1 === options.length && (_jsx(InfiniteScroll, { onView: onInfiniteScroll, loading: infiniteScrollLoading }))] }, option.value));
                } }));
        }
        else {
            return options.map((option) => {
                const optionsProps = getOptionProps(option);
                return (_jsx(Fragment, { children: option?.render?.(optionsProps) || renderOption?.(optionsProps) }, option.value));
            });
        }
    };
    const groupedOptions = useMemo(() => {
        if (Array.isArray(groups)) {
            return groups
                .map((group) => ({
                ...group,
                options: options.filter((opt) => opt.group === group.id),
            }))
                .filter((group) => group.options.length > 0);
        }
        else {
            return null;
        }
    }, [groups, options]);
    return (
    // We turn off controlling highlight option by keyboard when user use mouse
    _jsxs(Wrapper, { onMouseMove: () => setControlledHighlight(false), ref: dropdownRef, "data-grouped": groupedOptions !== null, children: [groupedOptions
                ? groupedOptions.map((group) => (_jsxs(Fragment, { children: [_jsx(Group, { children: group.label }), getOptions(group.options)] }, group.id)))
                : getOptions(options), !virtualRender && (_jsx(InfiniteScroll, { onView: onInfiniteScroll, loading: infiniteScrollLoading }))] }));
};
const Wrapper = styled.div `
  &[data-grouped="true"] {
    > [role="option"] {
      padding-left: 36px;
    }
  }
`;
const Group = styled.div `
  ${cssBody400Bold};
  color: ${(p) => p.theme.systemColors.black};
  display: flex;
  align-items: center;
  padding: 8px;

  &:not(:first-child) {
    padding-top: 16px;
    border-top: 1px solid ${(p) => p.theme.systemColors.grey200};
    margin-top: 8px;
  }
`;
export const SelectDropdownOptions = Object.assign(_SelectDropdownOptions, {
    Wrapper,
    Group,
});
